import React from 'react';
import { AlertTriangle, UserPlus, Upload, Plus, Search } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';

export const StatusChangeDialog = ({ open, statusDialog, docStatus, statusComment, onCommentChange, onConfirm, onClose }) => (
  <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
    <DialogContent className="max-w-md">
      <DialogHeader>
        <DialogTitle>Alterar Status</DialogTitle>
        <DialogDescription>
          Alterar de <strong>{docStatus}</strong> para <strong>{statusDialog?.status}</strong>
        </DialogDescription>
      </DialogHeader>
      <div className="space-y-4">
        <div>
          <Label>Comentario (opcional)</Label>
          <Textarea value={statusComment} onChange={(e) => onCommentChange(e.target.value)} placeholder="Justificativa ou observacao..." rows={3} />
        </div>
        <div className="flex gap-2 justify-end">
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button className={`${statusDialog?.color} text-white`} onClick={() => onConfirm(statusDialog?.status)} data-testid="confirm-status-change">Confirmar</Button>
        </div>
      </div>
    </DialogContent>
  </Dialog>
);

export const DeleteDialog = ({ open, docTitle, onConfirm, onClose }) => (
  <Dialog open={open} onOpenChange={onClose}>
    <DialogContent className="max-w-md">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-red-600" />Excluir Documento
        </DialogTitle>
        <DialogDescription>
          Tem certeza que deseja excluir o documento <strong>{docTitle}</strong>? Esta acao nao pode ser desfeita.
        </DialogDescription>
      </DialogHeader>
      <div className="flex gap-2 justify-end pt-4">
        <Button variant="outline" onClick={() => onClose(false)}>Cancelar</Button>
        <Button className="bg-red-600 hover:bg-red-700 text-white" onClick={onConfirm} data-testid="confirm-delete-document">Excluir Documento</Button>
      </div>
    </DialogContent>
  </Dialog>
);

export const AssignDialog = ({ open, users, selectedAssignee, onSelect, onConfirm, onClose }) => (
  <Dialog open={open} onOpenChange={onClose}>
    <DialogContent className="max-w-md">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <UserPlus className="w-5 h-5 text-[#AF2628]" />Atribuir Responsavel
        </DialogTitle>
        <DialogDescription>Selecione o usuario responsavel por revisar ou acompanhar este documento.</DialogDescription>
      </DialogHeader>
      <div className="space-y-4 pt-2">
        <div>
          <Label className="text-sm">Responsavel</Label>
          <Select value={selectedAssignee} onValueChange={onSelect}>
            <SelectTrigger className="mt-1" data-testid="assignee-select"><SelectValue placeholder="Selecione um usuario" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Nenhum (remover atribuicao)</SelectItem>
              {users.map((u) => (<SelectItem key={u.user_id} value={u.user_id}>{u.name} - {u.role}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex gap-2 justify-end">
          <Button variant="outline" onClick={() => onClose(false)}>Cancelar</Button>
          <Button className="bg-[#AF2628] hover:bg-[#961B22]" onClick={onConfirm} data-testid="confirm-assign-button">Confirmar</Button>
        </div>
      </div>
    </DialogContent>
  </Dialog>
);

export const UploadRevisionDialog = ({ open, doc, uploadFile, uploading, onFileChange, onConfirm, onClose }) => (
  <Dialog open={open} onOpenChange={onClose}>
    <DialogContent className="max-w-md">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <Upload className="w-5 h-5 text-[#AF2628]" />Enviar Revisao Externa
        </DialogTitle>
        <DialogDescription>
          Envie um arquivo .docx editado fora da plataforma. Uma nova versao sera criada automaticamente (versao atual: v{doc?.current_version}).
        </DialogDescription>
      </DialogHeader>
      <div className="space-y-4 pt-2">
        <input type="file" accept=".docx" onChange={(e) => onFileChange(e.target.files[0])} className="block w-full text-sm text-[#6E6E73] file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-[#F7F7F8] file:text-[#111111] hover:file:bg-[#E5E5EA] file:cursor-pointer cursor-pointer border border-[#E5E5EA] rounded-lg p-2" data-testid="upload-revision-file-input" />
        {uploadFile && <p className="text-sm text-[#6E6E73]">Arquivo: <span className="font-medium">{uploadFile.name}</span> ({(uploadFile.size / 1024).toFixed(1)} KB)</p>}
        <div className="flex gap-2 justify-end">
          <Button variant="outline" onClick={() => onClose(false)}>Cancelar</Button>
          <Button className="bg-[#AF2628] hover:bg-[#961B22]" onClick={onConfirm} disabled={!uploadFile || uploading} data-testid="confirm-upload-revision">{uploading ? 'Enviando...' : 'Enviar Revisao'}</Button>
        </div>
      </div>
    </DialogContent>
  </Dialog>
);

export const AddItemDialog = ({ open, products, productSearch, selectedProduct, newItemQty, addingItem, onSearchChange, onSelectProduct, onQtyChange, onConfirm, onClose }) => (
  <Dialog open={open} onOpenChange={onClose}>
    <DialogContent className="max-w-lg">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2"><Plus className="w-5 h-5 text-[#AF2628]" />Adicionar Item ao Documento</DialogTitle>
        <DialogDescription>Selecione um produto e defina a quantidade. O preco sera buscado automaticamente da tabela de precos.</DialogDescription>
      </DialogHeader>
      <div className="space-y-4 pt-2">
        <div>
          <Label className="text-sm">Buscar Produto</Label>
          <div className="relative mt-1">
            <Search className="absolute left-3 top-2.5 w-4 h-4 text-[#AEAEB2]" />
            <Input placeholder="Digitar nome do produto..." value={productSearch} onChange={(e) => onSearchChange(e.target.value)} className="pl-9" data-testid="add-item-search" />
          </div>
        </div>
        <div className="max-h-48 overflow-y-auto border border-[#E5E5EA] rounded-lg">
          {products
            .filter(p => !productSearch || p.name.toLowerCase().includes(productSearch.toLowerCase()))
            .map((p) => (
              <div key={p.product_id} onClick={() => onSelectProduct(p)} className={`px-3 py-2.5 cursor-pointer border-b border-[#F7F7F8] last:border-0 transition-colors ${selectedProduct?.product_id === p.product_id ? 'bg-[#AF2628]/10 border-l-2 border-l-[#AF2628]' : 'hover:bg-[#F7F7F8]'}`} data-testid={`product-option-${p.product_id}`}>
                <div className="font-medium text-sm">{p.name}</div>
                {p.descricao_resumida && <div className="text-xs text-[#6E6E73]">{p.descricao_resumida}</div>}
              </div>
            ))}
          {products.filter(p => !productSearch || p.name.toLowerCase().includes(productSearch.toLowerCase())).length === 0 && (
            <div className="text-center py-4 text-sm text-[#AEAEB2]">Nenhum produto encontrado</div>
          )}
        </div>
        {selectedProduct && (
          <div className="bg-[#F7F7F8] rounded-lg p-3">
            <p className="text-sm font-medium text-[#111111]">Selecionado: {selectedProduct.name}</p>
            <div className="mt-2">
              <Label className="text-xs">Quantidade</Label>
              <Input type="number" min="1" step="1" value={newItemQty} onChange={(e) => onQtyChange(e.target.value)} className="mt-1 w-32 h-8 text-sm" data-testid="add-item-qty" />
            </div>
          </div>
        )}
        <div className="flex gap-2 justify-end">
          <Button variant="outline" onClick={() => onClose(false)}>Cancelar</Button>
          <Button className="bg-[#AF2628] hover:bg-[#961B22]" onClick={onConfirm} disabled={!selectedProduct || addingItem} data-testid="confirm-add-item">{addingItem ? 'Adicionando...' : 'Adicionar Item'}</Button>
        </div>
      </div>
    </DialogContent>
  </Dialog>
);
